import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, Phone, Clock, ArrowRight } from "lucide-react";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import ContactForm from "../components/ContactForm";
import { PageHeader } from "./Services";
import { ARTICLES, getArticle } from "../lib/articles";
import { PHOTOS } from "../lib/photos";
import { SITE } from "../lib/site";

// Map slug d'article → catégorie de photos à afficher contextuellement.
const ARTICLE_PHOTO_CAT = {
  "essaimage-termites-bordeaux-printemps": "Essaimage",
  "comment-savoir-si-maison-bordeaux-termites": "Cordonnets",
  "reconnaitre-termites-5-minutes": "Cordonnets",
  "injection-ou-pieges-traitement-termites": "Pièges-appâts",
  "humidite-termites-cave-bordelaise": "Cave & humidité",
  "termites-maisons-anciennes-pierre": "Cordonnets",
  "quartiers-bordeaux-touches-termites": "Cordonnets",
  "dangers-termites-maison": "Bois infesté",
  "termites-bordeaux-tout-savoir": "Essaimage",
  "calendrier-prevention-termites-gironde-saison-par-saison": "Essaimage",
  "traitement-termites-capricorne-garantie-5-ans-gironde": "Charpente",
  "prix-methodes-traitement-termites-communes-gironde": "Pièges-appâts",
  "franchise-termite-habitat-devenir-franchise": "Chantier",
};

// Rend la syntaxe [texte](url) : liens externes en <a>, internes en <Link>.
function RichText({ text }) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return part;
    const [, label, href] = m;
    if (href.startsWith("/")) return <Link key={i} to={href}>{label}</Link>;
    return (
      <a key={i} href={href} target="_blank" rel="noopener" data-testid="article-external-link">
        {label}
      </a>
    );
  });
}

export function BlogList() {
  return (
    <div data-testid="blog-page">
      <SeoHead
        title={`Blog termites Bordeaux & Gironde — ${ARTICLES.length} articles experts | Termite Habitat 33`}
        description="Articles experts sur le traitement des termites et du capricorne, les prix en Gironde, la prévention saison par saison, la garantie 5 ans et la franchise Termite Habitat."
        canonical={SITE.url + "/blog"}
        keywords={["blog termites Bordeaux", "actualités termites Gironde"]}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Blog", url: SITE.url + "/blog" },
        ]}
      />
      <PageHeader
        eyebrow="Journal"
        title="Tout comprendre sur les termites à Bordeaux."
        intro={`${ARTICLES.length} articles d'expert pour identifier, prévenir et traiter les infestations en Gironde.`}
      />
      <section className="container-x py-20 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ARTICLES.map((a) => (
          <Link
            key={a.slug}
            to={`/blog/${a.slug}`}
            className="group block bg-white border border-[#D5CABD] hover:border-[#7A2E39] transition-colors rounded-sm overflow-hidden"
            data-testid={`article-card-${a.slug}`}
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img src={a.cover} alt={a.title}
                   className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-6">
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> {a.readingMinutes} min ·{" "}
                {new Date(a.date).toLocaleDateString("fr-FR")}
              </div>
              <h2 className="font-serif text-2xl text-gray-900 leading-snug mb-2">{a.title}</h2>
              <p className="text-sm text-gray-600 line-clamp-3">{a.excerpt}</p>
              <div className="mt-4 text-[#7A2E39] text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1">
                Lire l'article <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        ))}
      </section>
      <CTABand />
    </div>
  );
}

export function Article() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/blog" replace />;

  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`article-${slug}`}>
      <SeoHead
        title={`${article.title} | Termite Bordeaux & Habitat 33`}
        description={article.excerpt}
        canonical={`${SITE.url}/blog/${slug}`}
        image={article.cover}
        keywords={article.keywords}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Blog", url: SITE.url + "/blog" },
          { name: article.title, url: `${SITE.url}/blog/${slug}` },
        ]}
        schema={[{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          dateModified: article.date,
          image: article.cover,
          keywords: article.keywords.join(", "),
          author: { "@type": "Organization", name: SITE.legalName, url: SITE.parentSite },
          publisher: {
            "@type": "Organization",
            name: SITE.legalName,
            url: SITE.parentSite,
            logo: { "@type": "ImageObject", url: SITE.url + "/favicon.ico" },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.url}/blog/${slug}` },
        }, ...(article.faq ? [{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: article.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }] : [])]}
      />

      <section className="bg-[#E8DCC9]/40 border-b border-[#D5CABD]">
        <div className="container-x py-16 lg:py-20">
          <div className="text-xs text-gray-500 mb-6 flex items-center gap-1">
            <Link to="/" className="hover:text-gray-800">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-gray-800">Blog</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-800 line-clamp-1">{article.title}</span>
          </div>
          <div className="eyebrow mb-3">{article.readingMinutes} min de lecture</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-gray-900 leading-[1.05] mb-5 max-w-4xl">
            {article.title}
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl">{article.excerpt}</p>
        </div>
      </section>

      <section className="container-x py-12 grid lg:grid-cols-3 gap-12">
        <article className="lg:col-span-2 prose-article" data-testid="article-content">
          <img src={article.cover} alt={article.title} className="w-full h-[380px] object-cover rounded-sm mb-10" />
          {article.sections.map((s, i) => {
            if (s.type === "h2") return <h2 key={i}>{s.text}</h2>;
            if (s.type === "h3") return <h3 key={i}>{s.text}</h3>;
            if (s.type === "ul")
              return (
                <ul key={i}>
                  {s.items.map((it, j) => <li key={j}><RichText text={it} /></li>)}
                </ul>
              );
            return <p key={i}><RichText text={s.text} /></p>;
          })}

          {article.faq && (
            <div data-testid="article-faq">
              <h2>Questions fréquentes</h2>
              {article.faq.map((f, i) => (
                <div key={i}>
                  <h3>{f.q}</h3>
                  <p>{f.a}</p>
                </div>
              ))}
            </div>
          )}

          <blockquote>
            Besoin d'une expertise rapide à Bordeaux ou en Gironde ? Appelez le {SITE.phone} ou
            <Link to="/contact"> demandez une expertise complète</Link>.
          </blockquote>

          <h2>Pour aller plus loin</h2>
          <p>
            Vous pensez avoir un problème de termites à Bordeaux ou en Gironde ? Demandez un{" "}
            <Link to="/devis">devis gratuit en ligne</Link> en 6 questions, ou appelez directement
            notre cabinet au {SITE.phone}.
          </p>

          {(() => {
            const cat = ARTICLE_PHOTO_CAT[slug];
            const ctxPhotos = cat ? PHOTOS.filter((p) => p.cat === cat).slice(0, 3) : [];
            if (ctxPhotos.length === 0) return null;
            return (
              <>
                <h2>Photos terrain — {cat}</h2>
                <p>
                  Voici des photos réelles prises sur nos chantiers en Gironde, illustrant le sujet
                  de cet article. Toute la photothèque est disponible sur la page{" "}
                  <Link to="/photos">Photos</Link>.
                </p>
                <div className="not-prose grid sm:grid-cols-2 lg:grid-cols-3 gap-4 my-6" data-testid="article-photos">
                  {ctxPhotos.map((p) => (
                    <figure key={p.src} className="border border-[#D5CABD] bg-white rounded-sm overflow-hidden">
                      <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                        <img src={p.src} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <figcaption className="p-3 text-xs text-gray-700">{p.title}</figcaption>
                    </figure>
                  ))}
                </div>
              </>
            );
          })()}
        </article>

        <aside className="lg:sticky lg:top-28 self-start space-y-5">
          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm shadow-sm">
            <div className="eyebrow mb-2">Expertise</div>
            <h3 className="font-serif text-2xl text-gray-900 mb-4">Une question ?</h3>
            <a href={`tel:${SITE.phoneRaw}`} className="btn-primary w-full mb-3" data-testid="article-sidebar-call">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
            <Link to="/contact" className="btn-ghost w-full">Demander une expertise</Link>
          </div>
          <div className="bg-[#E8DCC9]/40 border border-[#D5CABD] p-6 rounded-sm">
            <div className="eyebrow mb-3">À lire aussi</div>
            <ul className="space-y-3 text-sm">
              {others.map((o) => (
                <li key={o.slug}>
                  <Link to={`/blog/${o.slug}`} className="font-medium text-gray-900 hover:text-[#7A2E39]">
                    {o.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>

      <CTABand />

      <section className="container-x py-16">
        <h2 className="font-serif text-3xl text-gray-900 mb-6">Décrivez votre situation</h2>
        <div className="max-w-3xl">
          <ContactForm defaultSubject={`Question – ${article.title}`} />
        </div>
      </section>
    </div>
  );
}
